import { useEffect, useRef, useState } from 'react'
import { Type, Check } from 'lucide-react'
import { setDisplayPref, useDisplayPrefs } from '../lib/display-prefs'

/**
 * Text-size quick control in the app header — scales the interface TEXT
 * (never spacing or layout) through five levels:
 *
 *   compact 0.85 · normal 1 · large 1.15 · larger 1.3 · largest 1.5
 *
 * It writes the SAME account preference (`textSize`) as My Preferences — one
 * source of truth, saved to the server, so the choice follows the person to
 * any computer and never bleeds across accounts on a shared browser.
 * `display-prefs.js` mirrors it to `<html data-text-size>`, which index.css
 * turns into the `--font-size-scale` multiplier.
 *
 * UX shape mirrors `NotificationsBell` and `UserMenu` so the three
 * controls in the header sit visually consistent (square 36×36px
 * hit area, outside-click closes, soft hover).
 */
export function FontSizeMenu() {
  const { effective } = useDisplayPrefs()
  const [open, setOpen] = useState(false)
  const rootRef = useRef(null)

  const current = effective.textSize

  // Close on outside click (same pattern as the UserMenu) and on Escape,
  // returning focus to the trigger so a keyboard user isn't stranded.
  useEffect(() => {
    if (!open) return
    function onDocClick(e) {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOpen(false)
      }
    }
    function onKey(e) {
      if (e.key === 'Escape') {
        setOpen(false)
        rootRef.current?.querySelector('button')?.focus()
      }
    }
    document.addEventListener('mousedown', onDocClick)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDocClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  function pick(level) {
    void setDisplayPref('textSize', level)
    setOpen(false)
  }

  return (
    <div className="relative" ref={rootRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        title="Text size"
        className="inline-flex items-center justify-center h-9 w-9 rounded-md text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-ink-strong)] hover:bg-[color:var(--color-card-hover)] cursor-pointer transition-colors"
      >
        <Type size={16} strokeWidth={1.75} />
      </button>

      {open ? (
        <div
          role="menu"
          className="absolute right-0 top-full mt-2 w-48 rounded-lg border border-[color:var(--color-rule)] bg-[color:var(--color-card)] shadow-lg p-1 z-50"
        >
          {/* Caption — clarifies the section so a user who opened the
           *  menu by accident understands what these three options
           *  affect. Matches the small-caps caption pattern used in
           *  the UserMenu / NotificationsBell dropdowns. */}
          <p className="px-3 pt-2 pb-1 text-[11px] uppercase tracking-wide text-[color:var(--color-ink-subtle)] m-0">
            Text size
          </p>
          {FONT_SIZE_OPTIONS.map((opt) => {
            const active = current === opt.level
            return (
              <button
                key={opt.level}
                type="button"
                role="menuitemradio"
                aria-checked={active}
                onClick={() => pick(opt.level)}
                className={
                  'w-full flex items-center justify-between gap-3 px-3 py-2 rounded-md text-sm cursor-pointer transition-colors ' +
                  (active
                    ? 'bg-[color:var(--color-accent-bg)] text-[color:var(--color-accent-strong)] font-medium'
                    : 'text-[color:var(--color-ink)] hover:bg-[color:var(--color-card-hover)]')
                }
              >
                <span className="flex items-center gap-2">
                  {/* Sample-glyph preview — the "A" renders at the
                   *  size the option will produce, so a user can
                   *  visually compare the three without picking one
                   *  to test. Same trick the macOS Mail "make text
                   *  bigger" preferences uses. */}
                  <span
                    aria-hidden="true"
                    className="font-semibold text-[color:var(--color-ink-strong)] inline-block w-4 text-center leading-none"
                    style={{ fontSize: `${opt.sampleSize}px` }}
                  >
                    A
                  </span>
                  {opt.label}
                </span>
                {active ? <Check size={14} /> : null}
              </button>
            )
          })}
        </div>
      ) : null}
    </div>
  )
}

// The five account text-size levels, labelled as on My Preferences.
const FONT_SIZE_OPTIONS = [
  { level: 'compact', label: 'Compact', sampleSize: 11 },
  { level: 'normal', label: 'Normal', sampleSize: 13 },
  { level: 'large', label: 'Large', sampleSize: 15 },
  { level: 'larger', label: 'Larger', sampleSize: 17 },
  { level: 'largest', label: 'Largest', sampleSize: 19 },
]
